/**
 * Collaboration Requests Page
 * Shows all pending collaboration requests for the logged-in user.
 * Protected — redirects to login if not authenticated.
 * Route: /collaborations/requests
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import Navbar from '../components/Navbar';

const formatDate = (str) => {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const POST_TYPE_LABELS = {
  event: 'Event',
  opportunity: 'Opportunity',
  article: 'Article'
};

const POST_TYPE_PATHS = {
  event: 'events',
  opportunity: 'opportunities',
  article: 'articles'
};

const CollaborationRequestsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState({}); // collabId -> true while submitting

  useEffect(() => {
    if (user === null) {
      navigate('/login');
      return;
    }
    if (user) fetchRequests();
  }, [user]);

  const fetchRequests = async () => {
    try {
      const res = await apiClient.get('/collaborations/pending');
      setRequests(res.data.requests || []);
    } catch (err) {
      setError('Failed to load collaboration requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (collab, status) => {
    setActionLoading(prev => ({ ...prev, [collab.collab_id]: true }));
    try {
      await apiClient.put(`/collaborations/${collab.post_type}/${collab.collab_id}/status`, { status });
      // Remove from list after action
      setRequests(prev => prev.filter(r => r.collab_id !== collab.collab_id));
    } catch (err) {
      alert(err.response?.data?.error || `Failed to ${status} request.`);
    } finally {
      setActionLoading(prev => ({ ...prev, [collab.collab_id]: false }));
    }
  };

  if (loading) {
    return <div style={{ padding: '60px', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ maxWidth: '900px', margin: '40px auto', padding: '0 20px 60px', paddingTop: '140px' }}>
<Navbar />
      <button
        onClick={() => navigate(-1)}
        style={{ marginBottom: '24px', padding: '8px 18px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
      >
        ← Back
      </button>

      <h1 style={{ fontSize: '2.2rem', fontWeight: 'bold', marginBottom: '32px' }}>
        Collaboration Requests
      </h1>

      {error && (
        <div style={{ backgroundColor: '#fee', color: '#c00', padding: '12px', borderRadius: '4px', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      {requests.length === 0 && !error && (
        <div style={{ color: '#888', fontStyle: 'italic', fontSize: '16px', marginTop: '40px', textAlign: 'center' }}>
          No pending collaboration requests.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
        {requests.map(collab => (
          <div
            key={`${collab.post_type}-${collab.collab_id}`}
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '24px',
              alignItems: 'center',
              borderBottom: '1px solid #ccc',
              paddingBottom: '24px'
            }}
          >
            {/* Left: post info */}
            <div>
              <div style={{ fontSize: '1.15rem', fontWeight: 'bold', marginBottom: '6px' }}>
                <span style={{
                  display: 'inline-block',
                  fontSize: '11px',
                  fontWeight: 'normal',
                  backgroundColor: '#3a2410',
                  color: 'white',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                  marginRight: '10px',
                  verticalAlign: 'middle'
                }}>
                  {POST_TYPE_LABELS[collab.post_type]}
                </span>
                {collab.post_title || 'Untitled'}
                {collab.creator_name && (
                  <span style={{ fontWeight: 'normal', fontSize: '0.9rem', color: '#555', marginLeft: '8px' }}>
                    by {collab.creator_name}
                  </span>
                )}
              </div>

              {collab.post_description && (
                <div style={{ fontSize: '14px', color: '#444', marginBottom: '6px' }}>
                  {collab.post_description}
                </div>
              )}

              <div style={{ fontSize: '13px', color: '#666', marginBottom: '8px' }}>
                {collab.city && collab.state && `${collab.city}, ${collab.state}`}
                {collab.event_date && ` · ${formatDate(collab.event_date)}`}
                {` · Requested ${formatDate(collab.created_at)}`}
              </div>

              <button
                onClick={() => navigate(`/${POST_TYPE_PATHS[collab.post_type]}/${collab.post_id}`)}
                style={{ fontSize: '13px', color: '#007bff', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
              >
                View Post →
              </button>
            </div>

            {/* Right: accept/reject */}
            <div style={{ textAlign: 'right', minWidth: '240px' }}>
              <div style={{ fontSize: '13px', color: '#333', marginBottom: '12px', fontStyle: 'italic' }}>
                Would you like this post to appear on your account profile?
              </div>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => handleAction(collab, 'accepted')}
                  disabled={actionLoading[collab.collab_id]}
                  style={{
                    padding: '10px 28px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    backgroundColor: actionLoading[collab.collab_id] ? '#aaa' : 'white',
                    color: '#222',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    cursor: actionLoading[collab.collab_id] ? 'not-allowed' : 'pointer'
                  }}
                >
                  Yes
                </button>
                <button
                  onClick={() => handleAction(collab, 'rejected')}
                  disabled={actionLoading[collab.collab_id]}
                  style={{
                    padding: '10px 28px',
                    fontSize: '15px',
                    fontWeight: 'bold',
                    backgroundColor: actionLoading[collab.collab_id] ? '#aaa' : 'white',
                    color: '#222',
                    border: '2px solid #333',
                    borderRadius: '8px',
                    cursor: actionLoading[collab.collab_id] ? 'not-allowed' : 'pointer'
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CollaborationRequestsPage;
